/* eslint-disable @typescript-eslint/no-explicit-any */
import { browser } from '$app/environment';
import { getCurrentLocation } from './js/utils/helpers';
import { closeSidebar, darkModeStorage } from '$lib/stores/layouts';
import Breakpoint from './js/services/breakpoint';
import ResizeObserver from 'resize-observer-polyfill';
import SimpleBar from 'simplebar';

// DARK MODE VARIABLE
export const DARKMODE_KEY = 'dark-mode';
export const DARKMODE_VALUE = 'dark';
export const DARKMODE_CLASS = 'dark';

// SIDEBAR TOGGLE
export const SIDEBAR_CLASS = 'sidebar';
export const SIDEBAR_OPEN_CLASS = 'is-sidebar-open';

// SIDEBAR NAV
export const SIDEBAR_NAV_LINK_CLASS = 'nav-link';
export const SIDEBAR_NAV_PARENT_CLASS = 'nav-parent';
export const SIDEBAR_NAV_WRAPPER = 'nav-wrapper';

// PRELOADER
const PRELOADER_CLASS = 'app-preloader';

export const initLineone = async () => {
	if (browser) {
		const app = await import('$lib/lineone/js/app.js');
		await import('$lib/lineone/js/libs/forms.js');

		window.App = new app.default();
		window.dispatchEvent(new CustomEvent('app:mounted'));
	}
};

export const initDarkMode = () => {
	if (browser) {
		const initial = localStorage.getItem(DARKMODE_KEY) !== null ? localStorage.getItem(DARKMODE_KEY) : '';

		localStorage.setItem('dark-mode', initial || '');

		if (initial === DARKMODE_VALUE) {
			document.documentElement.classList.add(DARKMODE_CLASS);
			darkModeStorage.update(() => 'dark');
		} else {
			darkModeStorage.update(() => 'light');
		}
	}
};

export const initSidebar = () => {
	if (browser) {
		const _body = document.body;
		const _breakpoint = new Breakpoint();
		const _sidebar = document.querySelector(`.${SIDEBAR_CLASS}`);

		// checking the existing of the left sidebar
		if (!_sidebar) return;

		// Closing the sidebar at the breakponts smaller than "XL"
		if (!_breakpoint.xlAndUp) closeSidebar();

		// Closing the sidebar when breakpoint changed
		window.addEventListener('change:breakpoint', () => {
			if (_body.classList.contains(SIDEBAR_OPEN_CLASS)) closeSidebar();
		});
	}
};

export const initSidebarNav = async (elementNav: HTMLDivElement) => {
	// Select the navigation links
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const navLinks = document.querySelectorAll<any>(`.${SIDEBAR_NAV_LINK_CLASS}`);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const navParents = document.querySelectorAll<any>(`.${SIDEBAR_NAV_PARENT_CLASS}`);

	// checking the existing of the navigation links
	if (!(navLinks.length > 0)) return;

	// Declaring default opened parent active navigation link
	let openOnInit = null;

	// Add index for parent navigation links
	if (navParents) {
		navParents.forEach((node, i) => {
			node.dataset.navParentIndex = i;
		});
	}

	navLinks.forEach((node) => {
		// checking if the HTML element is link
		if (!node.href || !node.dataset.activeClass || !node.dataset.defaultClass) return;

		// checking if navigation link have parent
		const parent = node.parentNode?.closest(`.${SIDEBAR_NAV_PARENT_CLASS}`);

		// get active and default classes for navigation links from "data-active-class" and "data-default-class"
		const activeClass = node.dataset.activeClass.split(' ');
		const defaultClass = node.dataset.defaultClass.split(' ');

		// cleaning the "href" value of the navigation link
		const href = node.href.split('?')[0].split('#')[0];

		// comparing current link with location
		const currentLocation = getCurrentLocation();
		const lastRouteFronCurrLocation = currentLocation.split('//')[1].split('/');

		if (currentLocation.includes(href) && !currentLocation.includes('sales') && !currentLocation.includes('accounts')) {
			node.classList.add(...activeClass);
			setTimeout(() => node.scrollIntoView({ block: 'center' }));
			if (parent) {
				openOnInit = parseInt(parent.dataset.navParentIndex);
			}
		} else if (currentLocation.includes('sales')) {
			if (currentLocation === href) {
				node.classList.add(...activeClass);
				setTimeout(() => node.scrollIntoView({ block: 'center' }));
				if (parent) {
					openOnInit = parseInt(parent.dataset.navParentIndex);
				}
			}
		} else if (currentLocation.includes('accounts')) {
			if (currentLocation === href) {
				node.classList.add(...activeClass);
				setTimeout(() => node.scrollIntoView({ block: 'center' }));
				if (parent) {
					openOnInit = parseInt(parent.dataset.navParentIndex);
				}
			}
		} else {
			node.classList.add(...defaultClass);
		}
	});

	new (await import('accordion-js')).default(elementNav, {
		onlyChildNodes: false,
		duration: 200,
		openOnInit: [openOnInit]
	});

	window.ResizeObserver = ResizeObserver;
};

export const initSimpleSidebarNav = async (elementNav: HTMLDivElement) => {
	new SimpleBar(elementNav, { autoHide: true });
};

export const removeCloakClass = () => {
	if (browser) {
		const ROOT_APP_ID = 'root';

		const _root = document.querySelector(`#${ROOT_APP_ID}`);

		_root?.classList.remove('cloak');
	}
};

export const removeAppPreloader = () => {
	if (browser) {
		const preloader = document.querySelector(`.${PRELOADER_CLASS}`);

		if (!preloader) return;

		setTimeout(() => {
			preloader.classList.add('animate-[var(--ease-in-out)_fade-out_500ms_forwards]');
			setTimeout(() => preloader.remove(), 1000);
		}, 300);
	}
};
